import { useState, useMemo, useCallback } from "react";
import {
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  startOfDay,
  endOfDay,
  isSameDay,
  eachDayOfInterval,
} from "date-fns";

import { ViewMode, DayColumn, CalendarEvent } from "../types";

export function useCalendarNavigation() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>("workWeek");

  const goToDate = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  const navigate = useCallback(
    (direction: "prev" | "next" | "today") => {
      if (direction === "today") {
        setCurrentDate(new Date());
        return;
      }

      setCurrentDate((prev) => {
        if (viewMode === "day") {
          return direction === "next" ? addDays(prev, 1) : subDays(prev, 1);
        }
        return direction === "next" ? addWeeks(prev, 1) : subWeeks(prev, 1);
      });
    },
    [viewMode]
  );

  const dateRange = useMemo(() => {
    if (viewMode === "day") {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate),
      };
    }

    const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });

    if (viewMode === "workWeek") {
      // Seg a Sex
      const monday = addDays(weekStart, 1);
      const friday = addDays(weekStart, 5);
      return {
        start: startOfDay(monday),
        end: endOfDay(friday),
      };
    }

    // Semana completa: Dom a Sáb
    return {
      start: startOfDay(weekStart),
      end: endOfDay(endOfWeek(currentDate, { weekStartsOn: 0 })),
    };
  }, [currentDate, viewMode]);

  const headerTitle = useMemo(() => {
    const monthYear = currentDate.toLocaleDateString("pt-BR", {
      month: "long",
      year: "numeric",
    });
    return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
  }, [currentDate]);

  const getDayColumns = useCallback(
    (events: CalendarEvent[]): DayColumn[] => {
      const days = eachDayOfInterval({
        start: dateRange.start,
        end: dateRange.end,
      });

      const dayNames: Record<string, string> = {
        "0": "DOM",
        "1": "SEG",
        "2": "TER",
        "3": "QUA",
        "4": "QUI",
        "5": "SEX",
        "6": "SÁB",
      };

      return days.map((date) => {
        const dayEvents = events.filter((event) => {
          if (!event.start?.dateTime) return false;
          const eventDate = new Date(event.start.dateTime);
          return isSameDay(eventDate, date);
        });

        return {
          date,
          dayName: dayNames[date.getDay().toString()] || "",
          dayNumber: date.getDate(),
          isToday: isSameDay(date, new Date()),
          events: dayEvents,
        };
      });
    },
    [dateRange]
  );

  return {
    currentDate,
    viewMode,
    setViewMode,
    navigate,
    goToDate,
    dateRange,
    headerTitle,
    getDayColumns,
  };
}
